import { HubFeature } from '@ats/api/cm';
import { LocDef, LocDefs, LocMap, MapLocKeys } from '@cm/core/localization';

const pathStart = 'Home|Required Information|';
const sharedProperties: Pick<LocDef, 'shared' | 'portal'> = {
	shared: false,
	portal: 'CP',
};

export type CPCandidateRequirementsLocNames =
	| 'CPCandidateRequirementsAgreementInstruction'
	| 'CPCandidateRequirementsProfileInstruction'
	| 'CPCandidateRequirementsAddressInstruction'
	| 'CPCandidateRequirementsAffiliationInstruction'
	| 'CPCandidateRequirementsSettingsInstruction'
	| 'CPCandidateRequirementsAttributesInstruction'
	| 'CPCandidateRequirementsNameVerificationInstruction'
	| 'CPCandidateRequirementsPageTitle'
	| 'CPCandidateRequirementsAgreementTitle'
	| 'CPCandidateRequirementsNameConfirmationTitle'
	| 'CPCandidateRequirementsPersonalInformationTitle'
	| 'CPCandidateRequirementsMailingAddressTitle'
	| 'CPCandidateRequirementsAffiliationTitle'
	| 'CPCandidateRequirementsSettingsTitle'
	| 'CPCandidateRequirementsAttributesTitle'
	| 'CPCandidateRequirementsPageDescription';

export const cpCandidateRequirementsLocs: LocDefs<CPCandidateRequirementsLocNames> = {
	CPCandidateRequirementsAgreementInstruction: {
		defaultValue: '',
		description: 'Instructions for the credential agreement step in candidate requirements.',
		displayPath: `${pathStart}Credential Agreement Instruction`,
		displayType: 'Content',
		feature: HubFeature.CredentialAgreementAtLogin,
		htmlAllowed: true,
		legacyName:
			'Credential.Agreement.AtLogin/CandidateRequirements.CredentialAgreement.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsProfileInstruction: {
		defaultValue: '',
		description: 'Instructions for the profile information step in candidate requirements.',
		displayPath: `${pathStart}Profile Information Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfilePrimaryInformationRequireMinimum,
		htmlAllowed: true,
		legacyName:
			'CandidateProfile.PrimaryInformation.RequireMinimum/CandidateRequirements.ProfileInformation.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsAddressInstruction: {
		defaultValue: '',
		description: 'Instructions for the mailing address step in candidate requirements.',
		displayPath: `${pathStart}Address Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileAddressesPrimaryRequireMinimum,
		htmlAllowed: true,
		legacyName:
			'CandidateProfile.Addresses.Primary.RequireMinimum/CandidateRequirements.Address.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsAffiliationInstruction: {
		defaultValue: '',
		description: 'Instructions for the affiliation step in candidate requirements.',
		displayPath: `${pathStart}Affiliation Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliationRequired,
		htmlAllowed: true,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation.Required/CandidateRequirements.Affiliation.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsSettingsInstruction: {
		defaultValue: '',
		description: 'Instructions for the sponsor settings step in candidate requirements.',
		displayPath: `${pathStart}Sponsor Settings Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileCustomDataSettings,
		htmlAllowed: true,
		legacyName:
			'CandidateProfile.CustomData.Settings/CandidateRequirements.Settings.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsAttributesInstruction: {
		defaultValue: '',
		description: 'Instructions for the attributes step in candidate requirements.',
		displayPath: `${pathStart}Attributes Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateProfileCustomDataAttributes,
		htmlAllowed: true,
		...sharedProperties,
	},
	CPCandidateRequirementsNameVerificationInstruction: {
		defaultValue: `<p>Please update and confirm your name. Once confirmed, please contact support for any further name changes.</p>`,
		description: 'Instructions for the name confirmation step in candidate requirements.',
		displayPath: `${pathStart}Name Verification Instruction`,
		displayType: 'Content',
		feature: HubFeature.CandidateAccess,
		htmlAllowed: true,
		legacyName: 'CandidateAccess/CandidateRequirements.NameVerification.Content.Instruction',
		...sharedProperties,
	},
	CPCandidateRequirementsPageTitle: {
		defaultValue: 'Required Information',
		description: 'Required Information page title.',
		displayPath: `${pathStart}Page Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateAccess,
		htmlAllowed: false,
		legacyName: 'CandidateAccess/Home.RequiredInformation.Title.PageTitle',
		...sharedProperties,
	},
	CPCandidateRequirementsAgreementTitle: {
		defaultValue: 'Credentials Agreement',
		description: 'Title on the page and in the tab for the credentials agreement.',
		displayPath: `${pathStart}Credential Agreement Title`,
		displayType: 'Title',
		feature: HubFeature.CredentialAgreement,
		htmlAllowed: false,
		shared: true,
		legacyName: 'Credential.Agreement/Credentials.Agreement.Title.PageTitle',
		...sharedProperties,
	},
	CPCandidateRequirementsNameConfirmationTitle: {
		defaultValue: 'Name Confirmation',
		description: 'Page title on home when candidate has to confirm their name.',
		displayPath: `${pathStart}Name Confirmation Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateAccess,
		htmlAllowed: false,
		legacyName: 'CandidateAccess/System.Prerequisite.Title.PageTitle',
		...sharedProperties,
	},
	CPCandidateRequirementsPersonalInformationTitle: {
		defaultValue: 'Personal Information',
		description: 'Title for the personal information section.',
		displayPath: `${pathStart}Personal Information Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateProfile,
		htmlAllowed: false,
		legacyName: 'CandidateProfile/Profile.Information.Title.PersonalInformation',
		...sharedProperties,
	},
	CPCandidateRequirementsMailingAddressTitle: {
		defaultValue: 'Mailing Address',
		description: 'Title for the mailing address section.',
		displayPath: `${pathStart}Mailing Address Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateProfileAddressesPrimary,
		htmlAllowed: false,
		legacyName: 'CandidateProfile.Addresses.Primary/Profile.Addresses.Title.MailingAddress',
		...sharedProperties,
	},
	CPCandidateRequirementsAffiliationTitle: {
		defaultValue: 'Affiliation',
		description: 'Title for the affiliation section.',
		displayPath: `${pathStart}Affiliation Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateProfilePrimaryInformationAffiliation,
		htmlAllowed: false,
		legacyName:
			'CandidateProfile.PrimaryInformation.Affiliation/Profile.Affiliation.Title.PersonalInformation',
		...sharedProperties,
	},
	CPCandidateRequirementsSettingsTitle: {
		defaultValue: 'Sponsor Settings',
		description: 'Title for the sponsor settings section.',
		displayPath: `${pathStart}Sponsor Settings Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateProfileCustomDataSettings,
		htmlAllowed: false,
		shared: true,
		legacyName: 'CandidateProfile.CustomData.Settings/Profile.Settings.Title.PageTitle',
		...sharedProperties,
	},
	CPCandidateRequirementsAttributesTitle: {
		defaultValue: 'Attributes',
		description: 'Title for the attributes section.',
		displayPath: `${pathStart}Attributes Title`,
		displayType: 'Title',
		feature: HubFeature.CandidateProfileCustomDataAttributes,
		htmlAllowed: false,
		...sharedProperties,
	},
	CPCandidateRequirementsPageDescription: {
		defaultValue: '',
		description: 'Description for the required information page.',
		displayPath: `${pathStart}Page Description`,
		displayType: 'Content',
		feature: HubFeature.CandidateAccess,
		htmlAllowed: true,
		legacyName: 'CandidateAccess/Home.RequiredInformation.Content.PageDescription',
		...sharedProperties,
	},
};

export type CPCandidateRequirementsLocType = LocMap<CPCandidateRequirementsLocNames>;

export const LOC_KEYS = MapLocKeys<CPCandidateRequirementsLocNames>(cpCandidateRequirementsLocs);
